/**
 * Маршрутные ограждения.
 *
 * Интерфейс скрывает недоступное, но решение всегда принимает сервер: это
 * удобство, а не защита. Любой запрос проверяется заново в application service.
 */

import { Loader2Icon } from 'lucide-react';
import { Navigate, Outlet, useLocation, useParams } from 'react-router';
import { useSession } from '@/api/auth';
import { useProject } from '@/api/projects';
import { ErrorState } from '@/components/common/states';
import { AppShell } from '@/components/layout/AppShell';
import { ApiError } from '@/api/client';

function FullScreenLoader({ label }: { label: string }) {
  return (
    <div className="flex min-h-dvh flex-col items-center justify-center gap-3 text-muted-foreground">
      <Loader2Icon className="size-5 animate-spin" />
      <p className="text-xs">{label}</p>
    </div>
  );
}

/** Пускает дальше только вошедшего пользователя. */
export function RequireAuth() {
  const location = useLocation();
  const { data: session, isPending, isError, error, refetch } = useSession();

  if (isPending) return <FullScreenLoader label="Проверяем сессию" />;

  if (isError) {
    return (
      <div className="flex min-h-dvh items-center justify-center">
        <ErrorState error={error} onRetry={() => void refetch()} />
      </div>
    );
  }

  if (!session) {
    return <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />;
  }

  return <Outlet />;
}

/**
 * Оболочка приложения без выбранного проекта: список проектов, служебные экраны.
 */
export function AppLayout() {
  const { data: session } = useSession();
  if (!session) return null;

  return (
    <AppShell session={session}>
      <Outlet />
    </AppShell>
  );
}

/**
 * Оболочка проекта. Загружает проект, показывает боковую навигацию и передаёт
 * проект дочерним страницам через контекст маршрута.
 */
export function ProjectLayout() {
  const { projectId } = useParams<{ projectId: string }>();
  const { data: session } = useSession();
  const { data: project, isPending, isError, error, refetch } = useProject(projectId);

  if (!session) return null;
  if (isPending) return <FullScreenLoader label="Открываем проект" />;

  if (isError) {
    // Недоступный проект отвечает 404 и не отличается от несуществующего:
    // по интерфейсу нельзя установить, что проект существует.
    const notFound = error instanceof ApiError && error.status === 404;
    return (
      <AppShell session={session}>
        <ErrorState
          error={
            notFound
              ? new ApiError(
                  404,
                  'NOT_FOUND',
                  'Проект не найден',
                  undefined,
                  undefined,
                  'Возможно, он архивирован или вас исключили из числа участников.',
                )
              : error
          }
          onRetry={notFound ? undefined : () => void refetch()}
        />
      </AppShell>
    );
  }

  return (
    <AppShell session={session} project={project}>
      <Outlet context={{ project }} />
    </AppShell>
  );
}
